import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Box, 
  Button, 
  useTheme, 
  Typography, 
  Paper, 
  Alert, 
  CircularProgress 
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useAuth } from "../../contexts/AuthContext";

// Import the existing sections
import BasicInfoSection from "../UpdateDivesite/BasicInfoSection";
import LocationDetailsSection from "../UpdateDivesite/LocationDetailsSection";
import CoordinatesSection from "../UpdateDivesite/CoordinatesSection";
import DescriptionSection from "../UpdateDivesite/DescriptionSection";
import DiveDetailsSection from "../UpdateDivesite/DiveDetailsSection";
import RatingsAndSlidersSection from "../UpdateDivesite/RatingsAndSlidersSection";

const ENVIRONMENTS = [
  { value: "Unknown", label: "Unknown" },
  { value: "Ocean-sea", label: "Ocean-sea" },
  { value: "Lake-quarry", label: "Lake-quarry" },
  { value: "Cave-cavern", label: "Cave-cavern" },
  { value: "Under-ice", label: "Under-ice" },
  { value: "Other", label: "Other" },
  { value: "undefined", label: "undefined" },
];

const PLATFORMS = [
  { value: "beach-shore", label: "beach-shore" },
  { value: "pier", label: "pier" },
  { value: "small-boat", label: "small-boat" },
  { value: "charter-boat", label: "charter-boat" },
  { value: "live-aboard", label: "live-aboard" },
  { value: "barge", label: "barge" },
  { value: "other", label: "other" },
];

const PROVINCES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "DC", label: "District Of Columbia" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

const UpdateDivesiteStub = () => {
  const { stubId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userClaims } = useAuth(); // Use currentUser from context
  const theme = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [stubData, setStubData] = useState(null);
  const [formData, setFormData] = useState(null);

  // Fetch stub data
  useEffect(() => {
    const fetchStub = async () => {
      try {
        // Check if user is authenticated
        if (!currentUser) {
          throw new Error("User not authenticated");
        }

        console.log("Fetching stub data for ID:", stubId);
        console.log("Current user:", currentUser.email);
        
        const token = await currentUser.getIdToken(true); // Force refresh token
        console.log("Token obtained successfully");
        
        const response = await fetch(`/api/divesitestubs/${stubId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        
        console.log("Fetch response status:", response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error("Fetch error response:", errorText);
          throw new Error(`Failed to fetch stub data: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log("Stub data received:", data);
        setStubData(data);
        
        // Extract data with safe defaults
        const site = data.uddf?.divesite?.site || {};
        const geography = site.geography || {};
        const address = geography.address || {};
        const sitedata = site.sitedata || {};
        const metadata = data.metadata || {};
        const additionalInfo = metadata.additionalInfo || {};
        const ratings = metadata.ratings || {};
        const platform = data.uddf?.dive?.informationbeforedive?.platform || "other";
        
        setFormData({
          name: site.name || "",
          longitude: geography.longitude || 0,
          latitude: geography.latitude || 0,
          country: address.country || "",
          environment: site.environment || "Unknown",
          platform: platform,
          location: geography.location || "",
          city: address.city || "",
          province: address.province || "",
          divesitePara: site.notes?.para || "",
          diveDescription: site.shore?.notes?.para || "",
          oneliner: metadata.oneliner || "",
          depth: [
            sitedata.minimumdepth || 0,
            sitedata.maximumdepth || 30,
          ],
          surfaceSwimDistance: additionalInfo.surfaceSwimDistance || 0,
          slogValue: ratings.slog || 0,
        });
        
      } catch (err) {
        console.error("Error fetching stub:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (stubId && currentUser) {
      fetchStub();
    } else if (!currentUser) {
      setError("Please log in to edit dive site stubs");
      setLoading(false);
    }
  }, [stubId, currentUser]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    console.log("=== SUBMIT DEBUG ===");
    console.log("Current user:", currentUser?.email);
    console.log("Stub ID:", stubId);
    console.log("Form data:", formData);

    if (!currentUser) {
      setError("User not authenticated");
      setSaving(false);
      return;
    }

    const updateData = {
      uddf: {
        generator: { name: "DiveprojectStub", version: 0 },
        divesite: {
          site: {
            name: formData.name,
            environment: formData.environment,
            geography: {
              longitude: formData.longitude,
              latitude: formData.latitude,
              location: formData.location,
              address: {
                city: formData.city,
                province: formData.province,
                country: formData.country,
              },
            },
            sitedata: {
              minimumdepth: formData.depth[0],
              maximumdepth: formData.depth[1],
            },
            shore: { notes: { para: formData.diveDescription } },
            notes: { para: formData.divesitePara },
          },
        },
        dive: {
          informationbeforedive: { platform: formData.platform },
        },
      },
      metadata: {
        oneliner: formData.oneliner,
        ratings: { slog: formData.slogValue },
        additionalInfo: { surfaceSwimDistance: formData.surfaceSwimDistance },
      },
    };

    console.log("Update data:", updateData);

    try {
      const token = await currentUser.getIdToken(true); // Force refresh
      console.log("Token obtained for update");

      const url = `/api/divesitestubs/${stubId}`;
      console.log("PATCH URL:", url);

      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updateData),
      });

      console.log("PATCH response status:", response.status);
      console.log("PATCH response headers:", [...response.headers.entries()]);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("PATCH error response:", errorText);
        
        let errorMessage = `Failed to update stub: ${response.status} ${response.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          // If response isn't JSON, use the text
          errorMessage = errorText || errorMessage;
        }
        
        throw new Error(errorMessage);
      }

      const updatedStub = await response.json();
      console.log("Update successful:", updatedStub);
      setSuccess("Stub updated successfully!");
      setStubData(updatedStub);
      
    } catch (err) {
      console.error("Update error:", err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForReview = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    if (!currentUser) {
      setError("User not authenticated");
      setSaving(false);
      return;
    }

    try {
      const token = await currentUser.getIdToken(true);

      const response = await fetch(`/api/divesitestubs/${stubId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          stubInfo: {
            status: 'underreview'
          }
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Submit for review error:", errorText);
        
        let errorMessage = `Failed to submit for review: ${response.status} ${response.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          errorMessage = errorText || errorMessage;
        }
        
        throw new Error(errorMessage);
      }

      setSuccess("Stub submitted for review!");
      
      // Refresh the stub data to show updated status
      const updatedResponse = await fetch(`/api/divesitestubs/${stubId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (updatedResponse.ok) {
        const updatedData = await updatedResponse.json();
        setStubData(updatedData);
      }
      
    } catch (err) {
      console.error("Submit for review error:", err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading stub data...</Typography>
      </Box>
    );
  }

  // Show error if no user
  if (!currentUser) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Please log in to edit dive site stubs.
          <Button onClick={() => navigate('/login')} sx={{ ml: 2 }}>
            Go to Login
          </Button>
        </Alert>
      </Box>
    );
  }

  // Show error if no data
  if (!stubData || !formData) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Stub not found or failed to load. 
          {error && <><br />Error: {error}</>}
        </Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  const currentStatus = stubData.stubInfo?.status;
  const canEdit = ['claimed', 'rejected'].includes(currentStatus);
  const canSubmitForReview = currentStatus === 'claimed';

  return (
    <Box p={3} maxWidth="800px" mx="auto">
      <Typography variant="h4" gutterBottom>
        Edit Divesite Stub
      </Typography>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Stub Status: <strong>{currentStatus}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Stub ID: {stubId}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          User: {currentUser.email}
        </Typography>
        {currentStatus === 'rejected' && stubData.stubInfo?.rejectionReason && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>Rejection Reason:</strong> {stubData.stubInfo.rejectionReason}
          </Alert>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {!canEdit && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This stub cannot be edited in its current status: {currentStatus}
        </Alert>
      )}

      <Paper elevation={3} sx={{ p: 3 }}>
        <Box
          component="form"
          sx={{ 
            "& > :not(style)": { m: 1, pr: 2 },
            '& .MuiInputLabel-root': {
              color: theme.palette.text.primary,
            }
          }}
          noValidate
          autoComplete="off"
          onSubmit={handleSubmit}
        >
          <BasicInfoSection
            name={formData.name}
            location={formData.location}
            onChange={handleChange}
            disabled={!canEdit}
          />
          
          <LocationDetailsSection
            city={formData.city}
            province={formData.province}
            country={formData.country}
            provinces={PROVINCES}
            onChange={handleChange}
            disabled={!canEdit}
          />
          
          <CoordinatesSection
            latitude={formData.latitude}
            longitude={formData.longitude}
            onChange={handleChange}
            disabled={!canEdit}
          />
          
          <DescriptionSection
            divesitePara={formData.divesitePara}
            diveDescription={formData.diveDescription}
            oneliner={formData.oneliner}
            onChange={handleChange}
            disabled={!canEdit}
          />
          
          <DiveDetailsSection
            environment={formData.environment}
            platform={formData.platform}
            environments={ENVIRONMENTS}
            platforms={PLATFORMS}
            onChange={handleChange}
            disabled={!canEdit}
          />
          
          <RatingsAndSlidersSection
            depth={formData.depth}
            slogValue={formData.slogValue}
            surfaceSwimDistance={formData.surfaceSwimDistance}
            onChange={handleChange}
            disabled={!canEdit}
          />

          <Box sx={{ mt: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {canEdit && (
              <Button
                variant="contained"
                type="submit"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            )}
            
            {canSubmitForReview && (
              <Button
                variant="contained"
                color="secondary"
                onClick={handleSubmitForReview}
                disabled={saving}
              >
                Submit for Review
              </Button>
            )}
            
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              disabled={saving}
            >
              Back
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default UpdateDivesiteStub;
